import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useQuery } from 'react-query';

import QUERY_KEYS from 'config/api/QUERY_KEYS';
import statisticsApi from 'config/api/statistics/statistics';
import type { TemplatesDashboardRow } from 'config/api/statistics/statistics.types';
import type { ParamsType } from 'helpers/createUrlWithParams/createUrlWithParams';

type DashboardKpiState = {
  completionRate: number;
  sumAValidInstancesCount: number;
  sumKCompletedValidInstancesCount: number;
  sumTActionsLastYearCount: number;
} | null;

const useTemplatesDashboardData = (filterParams: ParamsType, pageSize: number) => {
  const [offset, setOffset] = useState(0);
  const [accumulatedRows, setAccumulatedRows] = useState<TemplatesDashboardRow[]>([]);
  const [dashboardKpi, setDashboardKpi] = useState<DashboardKpiState>(null);

  useEffect(() => {
    setOffset(0);
    setAccumulatedRows([]);
    setDashboardKpi(null);
  }, [filterParams]);

  const {
    data: pageData,
    error: queryError,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useQuery(
    [QUERY_KEYS.GET_STATISTICS_TEMPLATES_DASHBOARD, filterParams, offset],
    () => statisticsApi.getTemplatesDashboard({ ...filterParams, limit: pageSize, offset }),
    { keepPreviousData: false },
  );

  const isForbidden = Boolean(isError && axios.isAxiosError(queryError) && queryError.response?.status === 403);

  useEffect(() => {
    if (!pageData) return;
    if (offset === 0) {
      setAccumulatedRows(pageData.rows);
      setDashboardKpi({
        completionRate: pageData.completionRate,
        sumAValidInstancesCount: pageData.sumAValidInstancesCount,
        sumKCompletedValidInstancesCount: pageData.sumKCompletedValidInstancesCount,
        sumTActionsLastYearCount: pageData.sumTActionsLastYearCount,
      });
    } else {
      setAccumulatedRows(prev => [...prev, ...pageData.rows]);
    }
  }, [pageData, offset]);

  const hasNextPage =
    pageData != null &&
    (pageData.totalTemplateCount != null ? offset + pageData.rows.length < pageData.totalTemplateCount : pageData.rows.length === pageSize);

  const loadNextPage = useCallback(() => {
    setOffset(o => o + pageSize);
  }, [pageSize]);

  return {
    accumulatedRows,
    dashboardKpi,
    hasNextPage,
    isError,
    isFetching,
    isForbidden,
    isLoading,
    loadNextPage,
    offset,
    refetch,
  };
};

export default useTemplatesDashboardData;
